import type {BreadcrumbList, Graph, Organization, SoftwareApplication, Thing, WebSite} from 'schema-dts'

import {NOTABILITY_SAME_AS} from './brandLinks'
import {sharedAssetUrl, stripTrailingSlash} from './urls'

// Shared schema.org / JSON-LD builders used by every Notability web property.
// Kept deliberately free of i18n and design-system coupling: builders take
// already-resolved strings so this module never has to import `notability-design-system`
// (which depends on this package) or know about translation keys. Each consumer resolves its
// own copy and feature list, then calls these.

// Stable `@id`s for the cross-site Notability brand entity and product.
//
// Consolidation into one Knowledge-Graph entity requires every property to emit byte-identical
// `@id`s, so the anchor is `API_SERVICE_URL` — the one origin every property already configures
// and the one least likely to move (in deployed environments it is the bare notability.com /
// staging.notability.com origin that fronts all of them). Callers pass their own
// `config.API_SERVICE_URL`; nothing else may anchor these ids.
export function notabilityBrandIds(apiServiceUrl: string, idSuffix = '') {
  const base = stripTrailingSlash(apiServiceUrl)
  return {
    organization: `${base}/#organization`,
    softwareApplication: `${base}/#software${idSuffix}`
  }
}

const NOTABILITY_NAME = 'Notability'
const NOTABILITY_LEGAL_NAME = 'Ginger Labs, Inc.'
const NOTABILITY_FOUNDING_DATE = '2008'
const NOTABILITY_FOUNDING_LOCATION = 'Silicon Valley, California'
const NOTABILITY_KNOWS_ABOUT = [
  'Note-taking',
  'Digital handwriting',
  'Handwriting recognition',
  'Audio recording and transcription',
  'PDF annotation',
  'AI study tools',
  'Education technology'
]
const DEFAULT_APPLICATION_CATEGORY = 'ProductivityApplication'
// Every platform Notability ships on. `Windows` covers the Electron desktop shell, which also
// packages the macOS build alongside the native Mac app.
const DEFAULT_OPERATING_SYSTEM = 'iOS, iPadOS, macOS, Windows, Android, Web'

export interface NotabilityApplicationParams {
  // Distinguishes an audience-specific product node (business, education) from the default one, so
  // the two don't collide on a single `@id`.
  idSuffix?: string
  applicationCategory?: string
  operatingSystem?: string
  description?: string
  featureList?: string[]
  screenshotUrl?: string
  offers?: {priceCurrency: string; lowPrice: string}
}

export interface NotabilityBrandParams {
  // The property's own `config.API_SERVICE_URL`. See `notabilityBrandIds`.
  apiServiceUrl: string
  organizationDescription?: string
  application?: NotabilityApplicationParams
}

export interface NotabilityBrandNodes {
  ids: {organization: string; softwareApplication: string}
  organization: Organization
  softwareApplication: SoftwareApplication
}

// The two cross-site brand nodes, built in one place so no property can describe Notability
// slightly differently. Consumers choose only the optional enrichment (copy, feature list,
// screenshot, offers) — never the identity, name, or logo.
export function notabilityBrandNodes({
  apiServiceUrl,
  organizationDescription,
  application = {}
}: NotabilityBrandParams): NotabilityBrandNodes {
  const ids = notabilityBrandIds(apiServiceUrl, application.idSuffix)
  const canonicalOrigin = stripTrailingSlash(apiServiceUrl)
  return {
    ids,
    organization: {
      '@type': 'Organization',
      '@id': ids.organization,
      name: NOTABILITY_NAME,
      legalName: NOTABILITY_LEGAL_NAME,
      url: canonicalOrigin,
      logo: sharedAssetUrl(apiServiceUrl, 'nb-logo-full.png'),
      ...(organizationDescription ? {description: organizationDescription} : {}),
      foundingDate: NOTABILITY_FOUNDING_DATE,
      foundingLocation: {'@type': 'Place', name: NOTABILITY_FOUNDING_LOCATION},
      knowsAbout: [...NOTABILITY_KNOWS_ABOUT],
      // Deliberately unconditional, China pages included: `sameAs` asserts identity, not promotion,
      // and a per-region list would publish conflicting claims under the one `@id` this builder
      // exists to keep consistent. The China footer hiding some profiles is a UX choice, not this.
      sameAs: [...NOTABILITY_SAME_AS]
    },
    softwareApplication: {
      '@type': 'SoftwareApplication',
      '@id': ids.softwareApplication,
      name: NOTABILITY_NAME,
      operatingSystem: application.operatingSystem ?? DEFAULT_OPERATING_SYSTEM,
      applicationCategory: application.applicationCategory ?? DEFAULT_APPLICATION_CATEGORY,
      ...(application.description ? {description: application.description} : {}),
      ...(application.featureList ? {featureList: application.featureList} : {}),
      url: canonicalOrigin,
      publisher: {'@id': ids.organization},
      ...(application.screenshotUrl ? {screenshot: application.screenshotUrl} : {}),
      ...(application.offers
        ? {
            offers: {
              '@type': 'AggregateOffer',
              priceCurrency: application.offers.priceCurrency,
              lowPrice: application.offers.lowPrice
            }
          }
        : {})
    }
  }
}

export interface WebSiteParams {
  // This site's own WebSite `@id`, anchored on its own origin — unlike the brand nodes, each
  // property is a distinct WebSite.
  websiteId: string
  url: string
  name: string
  // The shared Organization `@id`, so every site publishes under one brand.
  publisherId: string
}

export function buildWebSite(params: WebSiteParams): WebSite {
  return {
    '@type': 'WebSite',
    '@id': params.websiteId,
    url: stripTrailingSlash(params.url),
    name: params.name,
    publisher: {'@id': params.publisherId}
  }
}

export interface BreadcrumbItem {
  name: string
  url: string
}

export function buildBreadcrumbList(items: BreadcrumbItem[]): BreadcrumbList {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  }
}

export function buildGraph(nodes: Thing[]): Graph {
  return {'@context': 'https://schema.org', '@graph': nodes}
}

// Serializes a graph for embedding in a <script> element. `<`, `>`, and `&` are escaped to their
// \uXXXX forms so that no string value in the graph can close the element early, whatever it holds.
// The escaping lives here rather than at the call sites so that guarantee doesn't depend on each
// caller knowing which of its values are attacker-controlled. Stays valid JSON.
export function structuredDataJson(graph: Graph): string {
  return JSON.stringify(graph).replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026')
}
